import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { hasPermission } from '../utils/permissionUtils';
import { getContentPreview, getFirstImageUrl, getContentStats } from '../utils/htmlUtils';
import AudioPlayer from './AudioPlayer';
import CommentSection from './CommentSection';

const NewsDisplay = ({ 
  maxItems = 3, 
  showCategories = true,
  compact = false,
  refreshTrigger = 0,
  onAddNews = null,
  onEditNews = null,
  onDeleteNews = null,
  defaultExpanded = true // Add prop to control initial state
}) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItem, setExpandedItem] = useState(null);
  const [showingAll, setShowingAll] = useState(false);
  const [displayLimit, setDisplayLimit] = useState(maxItems);
  const [sectionExpanded, setSectionExpanded] = useState(defaultExpanded); // Add section-level collapse state
  const [audioClips, setAudioClips] = useState([]);

  useEffect(() => {
    loadNewsItems();
    if (user?.id) {
      loadAudioClips();
    }
  }, [refreshTrigger, user]);

  const loadAudioClips = async () => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/audio-clips?operation=getAudioClips&userId=${user.id}`);
      
      if (response.ok) {
        const clipsData = await response.json();
        setAudioClips(clipsData);
      }
    } catch (err) {
      console.error('Failed to load audio clips for news display:', err);
    }
  };

  // Function to render content with embedded audio players
  const renderContentWithAudio = (content) => {
    if (!content) return null;
    
    const audioRegex = /\[audio:([^\]]+)\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let key = 0;
    
    while ((match = audioRegex.exec(content)) !== null) {
      // Add content before the audio markup
      if (match.index > lastIndex) {
        const beforeText = content.substring(lastIndex, match.index);
        parts.push(
          <span 
            key={`text-${key++}`} 
            dangerouslySetInnerHTML={{ __html: beforeText }}
          />
        );
      }
      
      // Add audio player
      const clipName = match[1];
      const clip = audioClips.find(c => c.name === clipName);
      parts.push(
        <div 
          key={`audio-${key++}`} 
          style={{ margin: '12px 0' }}
          onClick={(e) => e.stopPropagation()}
        >
          {clip ? (
            <AudioPlayer
              clipName={clip.name}
              fileUrl={clip.file_url}
              compact={false}
              showTitle={true}
            />
          ) : (
            <div style={{
              padding: '8px 12px',
              background: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#dc2626',
              fontSize: '14px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              ⚠️ Audio clip "{clipName}" not found
            </div>
          )}
        </div>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining content
    if (lastIndex < content.length) {
      const remainingText = content.substring(lastIndex);
      parts.push(
        <span 
          key={`text-${key++}`} 
          dangerouslySetInnerHTML={{ __html: remainingText }}
        />
      );
    }
    
    // If no audio markup found, just render the content normally
    if (parts.length === 0) {
      return (
        <div 
          dangerouslySetInnerHTML={{ __html: content }}
          onClick={(e) => {
            // Allow link clicks to propagate normally
            if (e.target.tagName === 'A') {
              e.stopPropagation();
            }
          }}
        />
      );
    }
    
    return (
      <div
        onClick={(e) => {
          // Allow link clicks to propagate normally
          if (e.target.tagName === 'A') {
            e.stopPropagation();
          }
        }}
      >
        {parts}
      </div>
    );
  };

  const loadNewsItems = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      // Show all statuses for admins, only published for everyone else
      const shouldFilterStatus = !(user?.role === 'admin' || user?.role === 'super-admin');
      
      const params = new URLSearchParams({
        operation: 'getContent',
        contentType: 'news',
        limit: 20, // Always fetch more items to enable "Show More"
        userId: user?.id || ''
      });
      
      // Only add status filter for non-admins
      if (shouldFilterStatus) {
        params.append('status', 'published');
      }
      
      const response = await fetch(`${API_BASE}/api/content?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        setNewsItems(data.content || []);
      } else {
        throw new Error('Failed to load news');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleShowMore = () => {
    setShowingAll(true);
    setDisplayLimit(20);
  };

  const handleShowLess = () => {
    setShowingAll(false);
    setDisplayLimit(maxItems);
    setExpandedItem(null); // Close any expanded items
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffMinutes < 1) return t('news.justNow');
    if (diffMinutes === 1) return t('news.minuteAgo');
    if (diffMinutes < 60) return t('news.minutesAgo', { minutes: diffMinutes });
    if (diffHours === 1) return t('news.hourAgo');
    if (diffHours < 24) return t('news.hoursAgo', { hours: diffHours });
    if (diffDays === 1) return t('news.yesterday');
    if (diffDays < 7) return t('news.daysAgo', { days: diffDays });
    
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'announcement': '📢',
      'event': '📅',
      'general': '📰',
      'performance': '🎭',
      'rehearsal': '🎵',
      'social': '👥'
    };
    return icons[category] || '📰';
  };

  const getCustomThumbnail = (item) => {
    try {
      if (!item?.metadata) return null;
      
      // Handle invalid metadata that might be "[object Object]" or other invalid JSON
      if (typeof item.metadata === 'string') {
        if (item.metadata === '[object Object]' || item.metadata === 'null' || item.metadata === 'undefined') {
          return null;
        }
        try {
          const metadata = JSON.parse(item.metadata);
          return metadata.customThumbnail || null;
        } catch {
          // If parsing fails, assume invalid metadata
          return null;
        }
      } else if (typeof item.metadata === 'object') {
        // Already an object
        return item.metadata.customThumbnail || null;
      }
      
      return null;
    } catch (error) {
      console.error('Error parsing metadata for item', item?.id, ':', error);
      return null;
    }
  };

  const renderThumbnail = (item, size = 64) => {
    const customThumbnail = getCustomThumbnail(item);
    const autoThumbnail = getFirstImageUrl(item.content_html || item.content_text);
    const stats = getContentStats(item.content_html);
    
    let thumbnailUrl = null;
    let fallbackIcon = getCategoryIcon(item.category);
    let backgroundColor = '#374151';
    
    if (customThumbnail) {
      if (customThumbnail.type === 'stock') {
        fallbackIcon = customThumbnail.icon.icon;
        backgroundColor = customThumbnail.icon.color + '20';
      } else if (customThumbnail.type === 'content') {
        thumbnailUrl = customThumbnail.url;
      } else if (customThumbnail.type === 'auto') {
        thumbnailUrl = autoThumbnail;
      }
    } else {
      // Fallback to auto-detect
      thumbnailUrl = autoThumbnail;
      if (stats.videoCount > 0) fallbackIcon = '🎬';
      else if (stats.imageCount > 0) fallbackIcon = '📷';
    }
    
    return (
      <div style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '8px',
        backgroundColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        border: '1px solid #4b5563',
        overflow: 'hidden'
      }}>
        {thumbnailUrl ? (
          <img 
            src={thumbnailUrl}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div style={{
          display: thumbnailUrl ? 'none' : 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6b7280',
          fontSize: '28px' // Increased from 20px
        }}>
          {fallbackIcon}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        backgroundColor: '#1f2937',
        borderRadius: '12px',
        border: '1px solid #374151',
        overflow: 'hidden',
        marginBottom: '20px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        /* Reserve consistent space to prevent layout shift */
        minHeight: '120px'
      }}>
        {/* Header - consistent with actual content */}
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
          color: '#ffffff',
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ 
            margin: 0, 
            fontSize: '18px', 
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            📰 {t('news.latestNews')}
          </h2>
        </div>
        
        {/* Loading content area */}
        <div style={{ 
          textAlign: 'center', 
          padding: '40px 20px'
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            border: '3px solid #374151',
            borderTop: '3px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ margin: 0, color: '#9ca3af', fontSize: '14px' }}>{t('news.loadingNews')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: '#1f2937',
        borderRadius: '12px',
        border: '1px solid #374151',
        overflow: 'hidden',
        marginBottom: '20px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        minHeight: '120px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
          color: '#ffffff',
          padding: '16px 20px'
        }}>
          <h2 style={{ 
            margin: 0, 
            fontSize: '18px', 
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            📰 {t('news.latestNews')}
          </h2>
        </div>
        
        <div style={{
          textAlign: 'center',
          padding: '24px 20px',
          color: '#f87171'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>⚠️</div>
          <p style={{ margin: 0, fontSize: '14px' }}>{t('news.errorLoading')}: {error}</p>
        </div>
      </div>
    );
  }

  if (newsItems.length === 0) {
    return (
      <div style={{
        backgroundColor: '#1f2937',
        borderRadius: '12px',
        border: '1px solid #374151',
        overflow: 'hidden',
        marginBottom: '20px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        minHeight: '120px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
          color: '#ffffff',
          padding: '16px 20px'
        }}>
          <h2 style={{ 
            margin: 0, 
            fontSize: '18px', 
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            📰 {t('news.latestNews')}
          </h2>
        </div>
        
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          color: '#9ca3af'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📰</div>
          <p style={{ margin: 0, fontSize: '16px', color: '#d1d5db' }}>{t('news.noNews')}</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>{t('news.noNewsDescription')}</p>
        </div>
      </div>
    );
  }

  const displayedItems = newsItems.slice(0, displayLimit);

  return (
    <div style={{
      backgroundColor: '#1f2937',
      borderRadius: '12px',
      border: '1px solid #374151',
      overflow: 'hidden',
      marginBottom: '20px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
    }}>
      {/* Header - Always visible and clickable */}
      <div 
        style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
          color: '#ffffff',
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
        onClick={() => setSectionExpanded(!sectionExpanded)}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
        }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <h2 style={{ 
            margin: 0, 
            fontSize: '18px', 
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            📰 {t('news.latestNews')}
          </h2>
          
          {/* Quick preview when collapsed */}
          {!sectionExpanded && newsItems.length > 0 && !loading && (
            <span style={{
              fontSize: '13px',
              color: 'rgba(255,255,255,0.9)',
              fontWeight: '500',
              marginLeft: '8px'
            }}>
              • {newsItems.length} {newsItems.length === 1 ? 'item' : 'items'}
            </span>
          )}
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {sectionExpanded && (
            <span style={{
              fontSize: '12px',
              color: 'rgba(255,255,255,0.8)',
              fontWeight: '500'
            }}>
{showingAll ? t('news.showingAll', { count: newsItems.length }) : t('news.showingCount', { showing: Math.min(maxItems, newsItems.length), total: newsItems.length })}
            </span>
          )}
          
          {/* Add News Button - only show when expanded */}
          {sectionExpanded && hasPermission(user, 'manage-news') && onAddNews && (
            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent header click
                onAddNews();
              }}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              <span style={{ fontSize: '14px' }}>➕</span>
              Add News
            </button>
          )}
          
          {/* Expand/collapse arrow */}
          <div style={{ 
            color: 'rgba(255,255,255,0.8)', 
            fontSize: '14px',
            transform: sectionExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>
            ▼
          </div>
        </div>
      </div>

      {/* Expanded Content - News Items */}
      {sectionExpanded && (
        <div style={{ padding: '0' }}>
        {displayedItems.map((item, index) => (
          <div
            key={item.id}
            style={{
              borderBottom: index < displayedItems.length - 1 ? '1px solid #374151' : 'none',
              padding: expandedItem === item.id ? '20px' : '12px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              backgroundColor: expandedItem === item.id ? '#374151' : 'transparent'
            }}
            onClick={(e) => {
              // Don't interfere with link clicks
              if (e.target.tagName === 'A' || e.target.closest('a')) {
                return; // Let the link handle the click naturally
              }
              e.stopPropagation(); // Prevent section collapse when clicking items
              setExpandedItem(expandedItem === item.id ? null : item.id);
            }}
            onMouseEnter={(e) => {
              if (expandedItem !== item.id) {
                e.currentTarget.style.backgroundColor = '#2d3748';
              }
            }}
            onMouseLeave={(e) => {
              if (expandedItem !== item.id) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            {expandedItem === item.id ? (
              // Expanded view - keep original layout
              <>
                {/* Header with Thumbnail */}
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'flex-start',
                  marginBottom: '8px'
                }}>
                  {/* Thumbnail */}
                  {renderThumbnail(item)}
                  
                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      marginBottom: '4px'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flex: 1
                      }}>
                        {showCategories && (
                          <span style={{ fontSize: '14px' }}>
                            {getCategoryIcon(item.category)}
                          </span>
                        )}
                        <h3 style={{
                          margin: 0,
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#f3f4f6',
                          lineHeight: '1.3'
                        }}>
                          {item.title || 'Untitled'}
                        </h3>
                        {item.status === 'draft' && (
                          <span style={{
                            fontSize: '10px',
                            fontWeight: '600',
                            color: '#f59e0b',
                            backgroundColor: '#451a03',
                            padding: '2px 6px',
                            borderRadius: '12px',
                            textTransform: 'uppercase',
                            border: '1px solid #92400e'
                          }}>
{t('news.draft')}
                          </span>
                        )}
                      </div>
                      
                      {/* Management buttons and collapse arrow for expanded view */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                        {/* Edit and Delete buttons for users with manage-news permission */}
                        {hasPermission(user, 'manage-news') && (
                          <>
                            {/* Edit button */}
                            {onEditNews && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditNews(item);
                                }}
                                style={{
                                  backgroundColor: 'transparent',
                                  color: '#6b7280',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '4px',
                                  fontSize: '16px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s ease',
                                  width: '28px',
                                  height: '28px'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = '#374151';
                                  e.target.style.color = '#3b82f6';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.color = '#6b7280';
                                }}
                                title="Edit news item"
                              >
                                ✏️
                              </button>
                            )}
                            
                            {/* Delete button */}
                            {onDeleteNews && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`Delete "${item.title}"?`)) {
                                    onDeleteNews(item.id);
                                  }
                                }}
                                style={{
                                  backgroundColor: 'transparent',
                                  color: '#6b7280',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '4px',
                                  fontSize: '16px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s ease',
                                  width: '28px',
                                  height: '28px'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = '#374151';
                                  e.target.style.color = '#ef4444';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.color = '#6b7280';
                                }}
                                title="Delete news item"
                              >
                                🗑️
                              </button>
                            )}
                          </>
                        )}
                        
                        {/* Collapse arrow */}
                        <div style={{ 
                          color: '#6b7280', 
                          fontSize: '16px',
                          transform: 'rotate(180deg)',
                          transition: 'transform 0.2s ease',
                          marginLeft: '8px'
                        }}>
                          ▼
                        </div>
                      </div>
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#9ca3af',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flexWrap: 'wrap'
                    }}>
                      <span>{t('news.byAuthor', { author: item.author_name })}</span>
                      <span>•</span>
                      <span>{formatDate(item.created_at)}</span>
                      {item.category && (
                        <>
                          <span>•</span>
                          <span style={{ textTransform: 'capitalize' }}>{item.category}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Full Content */}
                <div style={{
                  color: '#d1d5db',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  marginTop: '12px'
                }}>
                  {item.content_html ? (
                    renderContentWithAudio(item.content_html)
                  ) : (
                    renderContentWithAudio(item.content_text)
                  )}
                </div>

                {/* Comments Section */}
                <div style={{ marginTop: '16px' }}>
                  <CommentSection 
                    contentId={item.id}
                    initialCommentCount={item.comment_count || 0}
                    onCommentCountChange={() => loadNewsItems()}
                  />
                </div>
              </>
            ) : (
              // Collapsed view - compact layout
              <div style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'flex-start'
              }}>
                {/* Thumbnail */}
                {renderThumbnail(item, 48)}
                
                {/* Main content area */}
                <div style={{ 
                  flex: 1, 
                  minWidth: 0,
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'flex-start'
                }}>
                  {/* Title and metadata column */}
                  <div style={{ width: '200px', flexShrink: 0 }}>
                    {/* Title row */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginBottom: '3px'
                    }}>
                      {showCategories && (
                        <span style={{ fontSize: '12px', flexShrink: 0 }}>
                          {getCategoryIcon(item.category)}
                        </span>
                      )}
                      <h3 style={{
                        margin: 0,
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#f3f4f6',
                        lineHeight: '1.2',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1
                      }}>
                        {item.title || 'Untitled'}
                      </h3>
                      {item.status === 'draft' && (
                        <span style={{
                          fontSize: '9px',
                          fontWeight: '600',
                          color: '#f59e0b',
                          backgroundColor: '#451a03',
                          padding: '1px 4px',
                          borderRadius: '8px',
                          textTransform: 'uppercase',
                          border: '1px solid #92400e',
                          flexShrink: 0
                        }}>
{t('news.draft')}
                        </span>
                      )}
                    </div>
                    
                    {/* Metadata row */}
                    <div style={{
                      fontSize: '11px',
                      color: '#9ca3af',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {t('news.byAuthor', { author: item.author_name })} • {formatDate(item.created_at)}
                      {item.category && ` • ${item.category}`}
                    </div>
                    
                    {/* Comment count row */}
                    {(item.comment_count || 0) > 0 && (
                      <div style={{
                        fontSize: '10px',
                        color: '#6b7280',
                        marginTop: '2px'
                      }}>
                        💬 {item.comment_count} {item.comment_count === 1 ? t('news.comments.comment') : t('news.comments.comments')}
                      </div>
                    )}
                  </div>
                  
                  {/* Text preview - uses the large middle space */}
                  <div style={{
                    backgroundColor: '#374151',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    flex: 1,
                    minWidth: 0,
                    border: '1px solid #4b5563'
                  }}>
                    <div style={{
                      color: '#d1d5db',
                      fontSize: '11px',
                      lineHeight: '1.3',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {getContentPreview(item.content_html || item.content_text, 200)}
                    </div>
                  </div>
                </div>
                
                {/* Management buttons and collapse arrow */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                  {/* Edit and Delete buttons for users with manage-news permission */}
                  {hasPermission(user, 'manage-news') && (
                    <>
                      {/* Edit button */}
                      {onEditNews && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditNews(item);
                          }}
                          style={{
                            backgroundColor: 'transparent',
                            color: '#6b7280',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            width: '24px',
                            height: '24px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#374151';
                            e.target.style.color = '#3b82f6';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.color = '#6b7280';
                          }}
                          title="Edit news item"
                        >
                          ✏️
                        </button>
                      )}
                      
                      {/* Delete button */}
                      {onDeleteNews && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete "${item.title}"?`)) {
                              onDeleteNews(item.id);
                            }
                          }}
                          style={{
                            backgroundColor: 'transparent',
                            color: '#6b7280',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            width: '24px',
                            height: '24px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#374151';
                            e.target.style.color = '#ef4444';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.color = '#6b7280';
                          }}
                          title="Delete news item"
                        >
                          🗑️
                        </button>
                      )}
                    </>
                  )}
                  
                  {/* Collapse arrow */}
                  <div style={{ 
                    color: '#6b7280', 
                    fontSize: '14px',
                    transform: 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                    padding: '2px'
                  }}>
                    ▼
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

          {/* Show More/Less Button */}
          {newsItems.length > maxItems && (
            <div style={{
              padding: '16px 20px',
              borderTop: '1px solid #374151',
              textAlign: 'center'
            }}>
            {!showingAll ? (
            <button
              onClick={handleShowMore}
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: '0 auto'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
{t('news.showMore')} ({newsItems.length - maxItems} {t('news.moreNews')})
              <span style={{ fontSize: '12px' }}>▼</span>
            </button>
          ) : (
            <button
              onClick={handleShowLess}
              style={{
                background: '#374151',
                color: '#d1d5db',
                border: '1px solid #4b5563',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#4b5563';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#374151';
              }}
            >
{t('news.showLess')}
              <span style={{ marginLeft: '8px', fontSize: '12px' }}>▲</span>
            </button>
          )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NewsDisplay;